import { useRef, useState } from 'react'
import { useEvent, useIsomorphicLayoutEffect } from 'react-use'
import { useFrame, useThree } from '@react-three/fiber'
import gsap from 'gsap'
import { EffectComposer } from 'postprocessing'
import { Box3, Mesh, Vector3 } from 'three'

import { useRouterStore } from '@/store/router'
import Background from '@/webgl/components/Background'
import Effects from '@/webgl/components/Effects'
import Gradient, { GradientHandler } from '@/webgl/components/Gradient'
import Landing, { LandingHandler } from '@/webgl/components/Landing'
import Spline from '@/webgl/components/Spline'
import { useWebGLStore } from '@/webgl/store/loader'
import { getViewportFromCameraFov } from '@/webgl/utils'

import { useLenisStore } from '@/contexts/lenis'
import { TRANSITION_ENTER_DURATION } from '@/contexts/transition'

const MainScene = () => {
  const landing = useRef<LandingHandler>(null!)
  const gradient = useRef<GradientHandler>(null!)
  const composer = useRef<EffectComposer>(null!)
  const background = useRef<Mesh>(null!)
  const spline = useRef<Mesh>(null!)

  const [bbox] = useState(() => new Vector3())
  const get = useThree(({ get }) => get)

  const needsGradient = useRef(true)
  const deferRender = useRef(true)

  const setup = () => {
    new Box3().setFromObject(spline.current).getSize(bbox)
  }

  const resize = () => {
    const camera = get().camera
    const viewport = getViewportFromCameraFov(camera, -bbox.z)
    background.current.position.setZ(-camera.position.z - bbox.z)
    background.current.scale.x = viewport.width
    background.current.scale.y = viewport.height
  }

  useFrame(({ gl }, delta) => {
    gl.clear()
    if (needsGradient.current) {
      gl.render(gradient.current.scene as any, gradient.current.camera as any)
    } else {
      if (!deferRender.current) {
        composer.current.render(delta)
      }

      gl.render(landing.current.scene as any, landing.current.camera as any)
    }

    if (!useWebGLStore.getState().compiled) {
      useWebGLStore.getState().setState({ compiled: true })
    }
  }, 1)

  useIsomorphicLayoutEffect(() => {
    const subscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      (route, prevRoute) => {
        const delay = route === prevRoute ? 0 : TRANSITION_ENTER_DURATION
        gsap.delayedCall(delay, () => {
          needsGradient.current = route !== '/'
        })
      },
      { fireImmediately: true }
    )

    return () => {
      subscribe()
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useLenisStore.subscribe(
      (state) => state.progress,
      () => {
        if (deferRender.current) {
          deferRender.current = false
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    setup()
    resize()
  }, [])

  useEvent('resize', resize)

  return (
    <>
      <Landing ref={landing} />

      <Gradient ref={gradient} />

      <Spline ref={spline as any}>
        <Background ref={background} />
      </Spline>

      <Effects ref={composer} />
    </>
  )
}

export default MainScene
